import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import MarkdownIt from 'markdown-it';

@Pipe({ name: 'markdown' })
export class MarkdownPipe implements PipeTransform {
    private md = this.createMarkdownRenderer();

    constructor(private sanitized: DomSanitizer) {}

    transform(value: string): SafeHtml {
        // Bypass Angular's HTML sanitizer so markdown links (<a>) are not stripped from [innerHTML].
        return this.sanitized.bypassSecurityTrustHtml(value ? this.md.render(value) : '');
    }

    private createMarkdownRenderer(): MarkdownIt {
        const md = new MarkdownIt({
            html: true,
            linkify: true,
        });
        const defaultLinkOpenRenderer =
            md.renderer.rules.link_open ?? ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

        md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
            const token = tokens[idx];
            const hrefAttrIndex = token.attrIndex('href');
            const targetAttrIndex = token.attrIndex('target');
            const relAttrIndex = token.attrIndex('rel');

            if (hrefAttrIndex >= 0) {
                const href = token.attrs![hrefAttrIndex][1];
                token.attrs![hrefAttrIndex][1] = this.toAbsoluteUrl(href);
            }

            if (targetAttrIndex < 0) {
                token.attrPush(['target', '_blank']);
            } else {
                token.attrs![targetAttrIndex][1] = '_blank';
            }

            if (relAttrIndex < 0) {
                token.attrPush(['rel', 'noopener noreferrer']);
            } else {
                token.attrs![relAttrIndex][1] = 'noopener noreferrer';
            }

            return defaultLinkOpenRenderer(tokens, idx, options, env, self);
        };

        return md;
    }

    /** Prefix https:// for protocol-less hrefs so they don't resolve against the app URL. */
    private toAbsoluteUrl(href: string): string {
        if (!href) {
            return href;
        }

        const trimmed = href.trim();

        if (
            trimmed.startsWith('#') ||
            trimmed.startsWith('//') ||
            /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
        ) {
            return trimmed;
        }

        return `https://${trimmed.replace(/^\/+/, '')}`;
    }
}
